import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconPhone, IconMail, IconLock, IconShield, IconArrowLeft, IconEye, IconEyeOff } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useAuthStore } from '../../../store/authStore';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import styles from './ForgotPasswordPage.module.scss';
import SideImage from '../../../components/auth/SideImage';
import LanguageSwitcher from '../../../components/common/LanguageSwitcher';
import Logo from '../../../components/common/Logo';

const inputCls =
  'w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white dark:bg-zinc-700 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-400';

function PasswordField({ value, onChange, placeholder, error, leftIcon }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      {leftIcon && (
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 z-10">
          {leftIcon}
        </span>
      )}
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`${inputCls} ${leftIcon ? 'pl-8' : ''} pr-10 ${error ? 'border-red-500 focus:ring-red-400' : ''}`}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setShow((v) => !v)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
      >
        {show ? <IconEyeOff size={16} /> : <IconEye size={16} />}
      </button>
    </div>
  );
}

/** 6-box OTP input */
function OtpBoxes({ value, onChange, onComplete, autoFocus, error }) {
  const refs = useRef([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] || '');

  const handleKey = (i, e) => {
    if (e.key === 'Backspace') {
      if (!digits[i] && i > 0) refs.current[i - 1]?.focus();
      const next = digits.map((d, idx) => (idx === i ? '' : d)).join('');
      onChange(next);
    }
  };

  const handleChange = (i, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    const next = digits.map((d, idx) => (idx === i ? char : d)).join('');
    onChange(next);
    if (char && i < 5) refs.current[i + 1]?.focus();
    if (next.length === 6 && /^\d{6}$/.test(next)) onComplete?.(next);
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted.padEnd(6, '').slice(0, 6));
    const focusIdx = Math.min(pasted.length, 5);
    refs.current[focusIdx]?.focus();
    if (pasted.length === 6) onComplete?.(pasted);
  };

  return (
    <div className="flex gap-2 justify-center" onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          autoFocus={autoFocus && i === 0}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKey(i, e)}
          className={`w-10 h-12 sm:w-12 sm:h-14 text-center text-lg font-bold border-2 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white dark:bg-zinc-800 dark:text-zinc-100 transition-colors ${
            error
              ? 'border-red-500'
              : d
              ? 'border-orange-400'
              : 'border-zinc-300 dark:border-zinc-600'
          }`}
        />
      ))}
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
}

function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const forgotPassword = useAuthStore((state) => state.forgotPassword);
  const verifyOtp = useAuthStore((state) => state.verifyOtp);
  const resetPassword = useAuthStore((state) => state.resetPassword);

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    identifier: '',
    otp: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(false);
  // Where the backend actually sent the reset code — it routes by the account's stored
  // country (null/BD → SMS, else email), so this is the source of truth for the UI copy.
  const [resetInfo, setResetInfo] = useState({ channel: 'sms', target: '' });

  // Whether the typed identifier looks like an email (used only for input hints/fallbacks;
  // the real SMS-vs-email decision is made server-side from the account's country).
  const identifierLooksLikeEmail = formData.identifier.includes('@');

  // The identifier sent to every reset call: { email } if it contains '@', else { phone }.
  const getIdentifier = () => {
    const value = formData.identifier.trim();
    return value.includes('@') ? { email: value } : { phone: value };
  };

  const validateStep1 = () => {
    const newErrors = {};
    const value = formData.identifier.trim();
    if (!value) {
      newErrors.identifier = t('validation.phoneOrEmailRequired');
    } else if (value.includes('@')) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) newErrors.identifier = t('auth.invalidEmail');
    } else if (!/^\+?[0-9]{6,15}$/.test(value)) {
      newErrors.identifier = t('validation.phoneInvalid');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.otp || formData.otp.length !== 6 || !/^\d{6}$/.test(formData.otp)) {
      newErrors.otp = t('validation.otpInvalid');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!formData.newPassword) {
      newErrors.newPassword = t('validation.passwordRequired');
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = t('validation.passwordTooShort');
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.confirmPasswordRequired');
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.passwordsDoNotMatch');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOtp = async () => {
    if (!validateStep1()) return;
    setLoading(true);
    try {
      const result = await forgotPassword(getIdentifier());
      // Trust the backend's routing (decided from the account's stored country). Fall back
      // to the local guess only if the response omits the channel/target.
      const channel = result?.channel || (identifierLooksLikeEmail ? 'email' : 'sms');
      const target = result?.maskedTarget || formData.identifier.trim();
      setResetInfo({ channel, target });
      notifications.show({
        title: t('auth.resetCodeSent'),
        message: channel === 'email' ? t('auth.checkEmailForResetCode') : t('auth.checkPhoneForResetCode'),
        color: 'green',
      });
      setStep(2);
      startOtpTimer();
    } catch (error) {
      console.error('Forgot password error:', error);
      // Mistyped identifier — surface it on the field instead of a generic "send failed".
      if (error.code === 'NO_ACCOUNT_FOUND') {
        setErrors((prev) => ({ ...prev, identifier: t('errors.NO_ACCOUNT_FOUND') }));
        notifications.show({ title: t('common.error'), message: t('errors.NO_ACCOUNT_FOUND'), color: 'red' });
        return;
      }
      // Blocked device / resend cooldown — the api interceptor already translated
      // these codes into error.message; show the real reason.
      if (error.code === 'ACCESS_RESTRICTED' || error.code === 'OTP_RESEND_COOLDOWN') {
        notifications.show({ title: t('common.error'), message: error.message, color: 'red' });
        return;
      }
      notifications.show({ title: t('common.error'), message: t('auth.failedToSendResetCode') || error.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (otpOverride) => {
    const otpValue = otpOverride ?? formData.otp;
    if (!otpOverride && !validateStep2()) return;
    setLoading(true);
    try {
      await verifyOtp(getIdentifier(), otpValue, 'password_reset');
      notifications.show({ title: t('auth.codeVerified'), message: t('auth.enterNewPassword'), color: 'green' });
      setStep(3);
    } catch (error) {
      console.error('OTP verification error:', error);
      notifications.show({ title: t('common.error'), message: t('auth.invalidCodeTryAgain') || error.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!validateStep3()) return;
    setLoading(true);
    try {
      await resetPassword(getIdentifier(), formData.otp, formData.newPassword);
      notifications.show({ title: t('common.success'), message: t('auth.passwordResetSuccess'), color: 'green' });
      navigate('/login');
    } catch (error) {
      console.error('Reset password error:', error);
      notifications.show({ title: t('common.error'), message: t('auth.failedToResetPassword') || error.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResendOtp) return;
    setLoading(true);
    try {
      await forgotPassword(getIdentifier());
      notifications.show({ title: t('auth.codeResent'), message: t('auth.newResetCodeSent'), color: 'green' });
      startOtpTimer();
    } catch (error) {
      console.error('Resend OTP error:', error);
      // Blocked device / resend cooldown — show the translated real reason.
      if (error.code === 'ACCESS_RESTRICTED' || error.code === 'OTP_RESEND_COOLDOWN') {
        notifications.show({ title: t('common.error'), message: error.message, color: 'red' });
        return;
      }
      notifications.show({ title: t('common.error'), message: t('auth.failedToResendCode') || error.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  const startOtpTimer = () => {
    setOtpTimer(300);
    setCanResendOtp(false);
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const progress = (step / 3) * 100;

  useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (otpTimer === 0 && step === 2) {
      setCanResendOtp(true);
    }
  }, [otpTimer, step]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={styles.container}>
      {/* Left Side - Branding */}
      <div className={styles.brandingSection}>
        <SideImage />
      </div>

      {/* Right Side - Form */}
      <div className={styles.formSection}>
        {/* language switcher */}
        <div className={styles.languageSwitcher}>
          <LanguageSwitcher />
        </div>

        <div className={styles.formContainer}>
          {/* logo */}
          <div className={styles.logo}>
            <Link to="/" className="text-orange-500">
              <Logo w={150} m="auto" />
            </Link>
          </div>

          <p className="text-center text-sm text-zinc-500 dark:text-zinc-400 mb-4">
            {t('auth.resetPassword')} ~ {t('auth.stepOf', { current: step, total: 3 })}
          </p>

          {/* Progress bar */}
          <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-2 mb-6">
            <div
              className="bg-orange-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Card */}
          <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-zinc-200 dark:border-zinc-700 p-5 sm:p-7">
            {/* ── Step 1: Enter phone or email ── */}
            {step === 1 && (
              <div className="flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    {t('auth.phoneOrEmail')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400">
                      {identifierLooksLikeEmail ? <IconMail size={16} /> : <IconPhone size={16} />}
                    </span>
                    <input
                      type="text"
                      placeholder={t('auth.phoneOrEmailPlaceholder')}
                      value={formData.identifier}
                      onChange={(e) => handleChange('identifier', e.target.value)}
                      className={`${inputCls} pl-8 ${errors.identifier ? 'border-red-500 focus:ring-red-400' : ''}`}
                    />
                  </div>
                  {errors.identifier && <p className="mt-1 text-xs text-red-500">{errors.identifier}</p>}
                  <p className="mt-1 text-xs text-zinc-400 dark:text-zinc-500">
                    {t('auth.phoneOrEmailDescription')}
                  </p>
                </div>

                <button
                  type="button"
                  disabled={loading}
                  onClick={handleSendOtp}
                  className="w-full px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-md font-medium text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {loading && <Spinner />}
                  {t('auth.sendResetCode')}
                </button>
              </div>
            )}

            {/* ── Step 2: OTP verification ── */}
            {step === 2 && (
              <div className="flex flex-col gap-4">
                {/* Header */}
                <div className="text-center">
                  <IconShield size={48} className="mx-auto mb-3" style={{ color: '#FF9900' }} />
                  <h4 className="font-semibold text-base text-zinc-800 dark:text-zinc-100 mb-1">
                    {t('auth.enterResetCode')}
                  </h4>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    {resetInfo.channel === 'email'
                      ? t('auth.resetCodeSentToEmail', { email: resetInfo.target })
                      : t('auth.resetCodeSentTo', { phone: resetInfo.target })}
                  </p>
                </div>

                {/* OTP boxes */}
                <div className="text-center">
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-3">
                    {t('auth.enterResetCodeInstruction')}
                  </p>
                  <OtpBoxes
                    value={formData.otp}
                    onChange={(val) => handleChange('otp', val)}
                    onComplete={(val) => handleVerifyOtp(val)}
                    autoFocus
                    error={!!errors.otp}
                  />
                  {errors.otp && <p className="mt-2 text-xs text-red-500">{errors.otp}</p>}
                </div>

                {otpTimer > 0 && (
                  <p className="text-center text-sm text-zinc-500 dark:text-zinc-400">
                    {t('auth.codeExpiresIn', { time: formatTime(otpTimer) })}
                  </p>
                )}

                {canResendOtp && (
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-sm text-zinc-500 dark:text-zinc-400">
                      {t('auth.didntReceiveCode')}
                    </span>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={handleResendOtp}
                      className="text-sm text-orange-500 hover:text-orange-600 font-medium disabled:opacity-60 flex items-center gap-1"
                    >
                      {loading && <Spinner />}
                      {t('auth.resendCode')}
                    </button>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex-1 px-4 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
                  >
                    {t('common.back')}
                  </button>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => handleVerifyOtp()}
                    className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {loading && <Spinner />}
                    {t('auth.verifyCode')}
                  </button>
                </div>
              </div>
            )}

            {/* ── Step 3: New password ── */}
            {step === 3 && (
              <form onSubmit={handleResetPassword}>
                <div className="flex flex-col gap-4">
                  {/* Header */}
                  <div className="text-center mb-2">
                    <IconLock size={48} className="mx-auto mb-3" style={{ color: '#FF9900' }} />
                    <h4 className="font-semibold text-base text-zinc-800 dark:text-zinc-100 mb-1">
                      {t('auth.createNewPassword')}
                    </h4>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">
                      {t('auth.enterStrongPassword')}
                    </p>
                  </div>

                  {/* New password */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                      {t('auth.newPassword')} <span className="text-red-500">*</span>
                    </label>
                    <PasswordField
                      value={formData.newPassword}
                      onChange={(e) => handleChange('newPassword', e.target.value)}
                      placeholder={t('auth.newPasswordPlaceholder')}
                      error={!!errors.newPassword}
                      leftIcon={<IconLock size={16} />}
                    />
                    {errors.newPassword && (
                      <p className="mt-1 text-xs text-red-500">{errors.newPassword}</p>
                    )}
                  </div>

                  {/* Confirm password */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                      {t('auth.confirmNewPassword')} <span className="text-red-500">*</span>
                    </label>
                    <PasswordField
                      value={formData.confirmPassword}
                      onChange={(e) => handleChange('confirmPassword', e.target.value)}
                      placeholder={t('auth.confirmNewPasswordPlaceholder')}
                      error={!!errors.confirmPassword}
                      leftIcon={<IconLock size={16} />}
                    />
                    {errors.confirmPassword && (
                      <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="flex-1 px-4 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
                    >
                      {t('common.back')}
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {loading && <Spinner />}
                      {t('auth.resetPassword')}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Back to login */}
          <div className="text-center mt-4">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm font-bold text-orange-500 hover:text-orange-600"
            >
              <IconArrowLeft size={16} />
              {t('auth.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
